import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UsersService } from '../../services/users.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginUser } from '../../interfaces/users.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { PersonsService } from '../../services/persons.service';
import { Person } from '../../interfaces/persons.interface';
import { PwaService } from '../../services/pwa.service';
import { MobileService } from '../../services/mobile.service';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm: FormGroup;
  returnUrl: string = '/dashboard';

  constructor(private fb: FormBuilder, private mobileService: MobileService
    , private personService: PersonsService, private usersService: UsersService, private snackBar: MatSnackBar, private route: ActivatedRoute, private router: Router, private pwaService: PwaService) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }


  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
  }


  onSubmit(): void {
    if (this.loginForm.valid) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userToken');
      localStorage.removeItem('userCard');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRefreshToken');
      const { email, password } = this.loginForm.value;

      // Prepare the user data
      const user: LoginUser = { username: email, email: email, password };

      // Call the UsersService to create the user
      this.usersService.login(user).subscribe({
        next: (response: any) => {
          if (response.access_token) {
            // Save tokens first so they're available for subsequent requests
            this.usersService.saveUserToken(response.access_token);
            this.usersService.saveUserRefreshToken(response.refresh_token);
            this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/karta';

            const mobileDevice = this.mobileService.getDeviceInfo();
            console.log('Mobile device info:', mobileDevice);
            if (mobileDevice?.platform) {
              this.mobileService.register(mobileDevice).subscribe({
                next: () => console.log('[Mobile] Device registered after login'),
                error: (err) => console.error('[Mobile] Registration after login failed:', err)
              });
            }

            // Get persons and select person/card
            this.personService.getCurrent().subscribe({
              next: (persons: Person[]) => {
                this.usersService.handlePersonAndCardSelection(persons, {
                  next: () => {
                    this.usersService.loggedIn.next(true);
                    this.snackBar.open('Użytkownik został zalogowany', 'Zamknij', {
                      duration: 2000,
                      panelClass: ['success-snackbar'],
                      horizontalPosition: 'center',
                      verticalPosition: 'bottom',
                    });
                    // Recheck PWA install eligibility after successful login
                    this.pwaService.recheckEligibility();
                    this.onLoginSuccess();
                  },
                  error: (err) => {
                    console.error('Error selecting person:', err);
                  },
                  complete: () => { }
                });
              },
              error: (err) => {
                console.error('Error fetching persons:', err);
                this.usersService.loggedIn.next(true);
              }
            });
          }

        },
        error: (err: any) => {
          let errorMessage = err.error?.detail || 'An error occurred';

          this.usersService.loggedIn.next(false); // 
          this.snackBar.open(errorMessage, 'Zamknij', {
            duration: 40000,
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center', // Center horizontally
            verticalPosition: 'bottom',
          });
        },
      });
    }
  }


  onLoginSuccess(): void {

    const isMobile = localStorage.getItem('mobileApp') === '1';
    if (isMobile) {
      const token = localStorage.getItem('userToken');
      if (token) {
        this.personService.getCurrent().subscribe({
          next: (persons) => {
            // Find the person marked as default with parent_id = 0 or null and active card
            let person = persons.find(p =>
              p.default === 1 &&
              (p.parent_id === 0 || p.parent_id === null) &&
              p.cards &&
              p.cards.some(card => [2, 3, 4].includes(card.status))
            );
            // Fallback: Find first person with parent_id = 0 or null and active card

            

            if (!person) {
              person = persons.find(p =>
                (p.parent_id === 0 || p.parent_id === null) &&
                p.cards &&
                p.cards.some(card => [2, 3, 4].includes(card.status))
              );
            }


            // Final fallback: use first person with parent_id = 0 or null
            if (!person) {
              person = persons.find(p => p.parent_id === 0 || p.parent_id === null) || persons[0];
            }
            if (
              person &&
              person.cards &&
              person.cards.some(card => [2, 3, 4].includes(card.status))
            ) {
              // Redirect to /karta if not already there
              if (this.router.url !== '/karta') {
                this.router.navigate(['/karta']);
              }
            }
          }
        });
      }
    }

    this.router.navigateByUrl(this.returnUrl);
  }

}
